import { CxpClient, CxpServiceLine, CxpTask, CxpTaskIntegration, CxpTaskStatus, CxpTaskType } from '../../models';
import { CrlTaskIntegration, CrlTaskRow } from '../cxp-crl-rest-api.service';

/**
 * Maps a backend `CrlTaskRow` (from `GET /cxp/tasks/list`) to the CXP-local
 * `CxpTask` model.
 *
 * The backend `TaskRow` was designed to mirror `CxpTask`: it already carries
 * the JOINED `serviceLine` + `clientName`, has done the per-signer SafeSend
 * expansion (one row per matching signer), and applies the user-scoping. So
 * this mapper is a straight field copy + enum translation — no client-side
 * fan-out, join, or re-expansion.
 */
export function mapTaskRowToCxpTask(row: CrlTaskRow): CxpTask {
  return {
    key: row.key,
    id: row.id,
    type: mapTaskRowType(row.type),
    sourceId: row.sourceId,
    name: row.name,
    description: row.description ?? '',
    createdAt: row.createdAt ?? null,
    dueDate: row.dueDate ?? null,
    updatedAt: row.updatedAt ?? null,
    lastViewedAt: row.lastViewedAt ?? null,
    status: mapTaskRowStatus(row.status),
    sourceStatus: row.sourceStatus,
    actionRequired: row.actionRequired,
    progress: row.progress ? { completed: row.progress.completed, total: row.progress.total } : null,
    staffIds: row.staffIds ?? [],
    clientId: row.clientId ?? 0,
    jobId: row.jobId ?? null,
    integration: mapTaskRowIntegration(row.integration),
    serviceLine: row.serviceLine ? { id: row.serviceLine.id, name: row.serviceLine.name } : null,
    clientName: row.clientName ?? null,
    // `TaskRow` carries the job id but not the job name, so the row subtitle
    // shows the client alone on this source.
    projectName: null,
  };
}

function mapTaskRowType(type: string): CxpTaskType {
  switch (type) {
    case 'DELIVERABLE':
      return 'deliverable';
    case 'EXTERNAL_DELIVERABLE':
      return 'external-deliverable';
    case 'EXTERNAL_CLIENT_REQUEST_LIST':
      return 'external-client-request-list';
    case 'CLIENT_REQUEST_LIST':
    default:
      return 'client-request-list';
  }
}

function mapTaskRowStatus(status: CrlTaskRow['status']): CxpTaskStatus {
  return status === 'COMPLETED' ? 'completed' : 'open';
}

/**
 * The backend already emits lowercase `safesend` / `fieldguide` and has done
 * the per-signer expansion — pass the signer fields straight through.
 */
function mapTaskRowIntegration(integration: CrlTaskIntegration | undefined): CxpTaskIntegration | null {
  if (!integration) return null;
  return {
    type: integration.type === 'fieldguide' ? 'fieldguide' : 'safesend',
    url: integration.url,
    signerGuid: integration.signerGuid,
    signerName: integration.signerName,
    signerLabel: integration.signerLabel,
    isSigned: integration.isSigned,
  };
}

/**
 * Fills `serviceLine` / `clientName` on rows that arrive without them.
 *
 * The REST rows are already joined by the backend, so this only runs on the
 * Hasura path, where the two fields come from a separate round trip. A job
 * belongs to one service line, so the first mapping for a `jobId` wins.
 */
export function enrichTasks(tasks: CxpTask[], serviceLines: CxpServiceLine[], clients: CxpClient[]): CxpTask[] {
  const serviceLineByJobId = new Map<number, { id: number; name: string }>();
  for (const sl of serviceLines) {
    for (const jobId of sl.jobIds) {
      serviceLineByJobId.set(jobId, { id: sl.id, name: sl.name });
    }
  }

  const clientNameById = new Map<number, string>();
  for (const client of clients) {
    clientNameById.set(client.id, client.name);
  }

  return tasks.map((task) => ({
    ...task,
    serviceLine: task.jobId !== null ? (serviceLineByJobId.get(task.jobId) ?? null) : null,
    clientName: clientNameById.get(task.clientId) ?? null,
  }));
}
